import Clarity from '@microsoft/clarity';

// Microsoft Clarity Configuration
const CLARITY_PROJECT_ID = 't84m325zqx';

// Initialize Clarity
export const initClarity = () => {
  try {
    Clarity.init(CLARITY_PROJECT_ID);
  } catch (error) {
    console.error('Failed to initialize Microsoft Clarity:', error);
  }
};

// Page view tracking
export const clarityTrackPageView = (path: string, title?: string) => {
  try {
    // Set custom page identifier
    const pageId = path.replace('/', '').replace(/\//g, '_') || 'home';
    const userId = `user_${Date.now()}`;
    
    Clarity.identify(userId, {
      sessionId: `session_${Date.now()}`,
      pageId: pageId,
      friendlyName: title || document.title
    });

    // Set page-specific tags
    Clarity.setTag('page_path', path);
    Clarity.setTag('page_title', title || document.title);
    Clarity.setTag('language', document.documentElement.lang || 'fr');
    
  } catch (error) {
    console.error('Clarity page tracking error:', error);
  }
};

// Language change tracking
export const clarityTrackLanguageChange = (fromLang: string, toLang: string) => {
  try {
    Clarity.event('language_change');
    Clarity.setTag('language_from', fromLang);
    Clarity.setTag('language_to', toLang);
    Clarity.setTag('language_current', toLang);
  } catch (error) {
    console.error('Clarity language tracking error:', error);
  }
};

// Form submission tracking
export const clarityTrackFormSubmission = (formName: string, formData?: any) => {
  try {
    Clarity.event('form_submission');
    Clarity.setTag('form_name', formName);
    Clarity.setTag('form_location', window.location.pathname);
    
    if (formData) {
      // Track form characteristics without PII
      if (formData.subject) Clarity.setTag('form_subject', formData.subject);
      if (formData.projectType) Clarity.setTag('project_type', formData.projectType);
      if (formData.budget) Clarity.setTag('budget_range', formData.budget);
      if (formData.vehicleBrand) Clarity.setTag('vehicle_brand', formData.vehicleBrand);
      if (formData.timeline) Clarity.setTag('project_timeline', formData.timeline);
      if (formData.hasFiles !== undefined) Clarity.setTag('has_attachments', formData.hasFiles.toString());
    }
  } catch (error) {
    console.error('Clarity form tracking error:', error);
  }
};

// CTA click tracking
export const clarityTrackCTAClick = (ctaName: string, location: string, destination?: string) => {
  try {
    Clarity.event('cta_click');
    Clarity.setTag('cta_name', ctaName);
    Clarity.setTag('cta_location', location);
    if (destination) Clarity.setTag('cta_destination', destination);
    
    // Mark this session as high value for upgrade
    if (ctaName.includes('Quote') || ctaName.includes('Project')) {
      Clarity.upgrade(`High value CTA clicked: ${ctaName}`);
    }
  } catch (error) {
    console.error('Clarity CTA tracking error:', error);
  }
};

// Service interaction tracking
export const clarityTrackServiceInteraction = (serviceName: string, action: string) => {
  try {
    Clarity.event('service_interaction');
    Clarity.setTag('service_name', serviceName);
    Clarity.setTag('interaction_type', action);
    Clarity.setTag('service_category', getServiceCategory(serviceName));
  } catch (error) {
    console.error('Clarity service tracking error:', error);
  }
};

// Hero carousel tracking
export const clarityTrackHeroInteraction = (slideIndex: number, slideType: string, action: string) => {
  try {
    Clarity.event('hero_interaction');
    Clarity.setTag('slide_index', slideIndex.toString());
    Clarity.setTag('slide_type', slideType);
    Clarity.setTag('hero_action', action);
  } catch (error) {
    console.error('Clarity hero tracking error:', error);
  }
};

// Tire configurator tracking
export const clarityTrackTireConfigurator = (step: string, value: any, isComplete: boolean = false) => {
  try {
    Clarity.event('tire_configurator');
    Clarity.setTag('configurator_step', step);
    Clarity.setTag('selected_value', value.toString());
    Clarity.setTag('configuration_complete', isComplete.toString());
    
    // Upgrade session if configuration is completed
    if (isComplete) {
      Clarity.upgrade('Tire configuration completed');
    }
  } catch (error) {
    console.error('Clarity tire configurator tracking error:', error);
  }
};

// FAQ interaction tracking
export const clarityTrackFAQInteraction = (questionId: string, question: string, category: string) => {
  try {
    Clarity.event('faq_interaction');
    Clarity.setTag('faq_question_id', questionId);
    Clarity.setTag('faq_category', category);
    Clarity.setTag('faq_question', question.substring(0, 50)); // Truncate for privacy
  } catch (error) {
    console.error('Clarity FAQ tracking error:', error);
  }
};

// Gallery interaction tracking
export const clarityTrackGalleryInteraction = (imageId: string, action: string, category?: string) => {
  try {
    Clarity.event('gallery_interaction');
    Clarity.setTag('image_id', imageId);
    Clarity.setTag('gallery_action', action);
    if (category) Clarity.setTag('image_category', category);
  } catch (error) {
    console.error('Clarity gallery tracking error:', error);
  }
};

// Brand click tracking
export const clarityTrackBrandClick = (brandName: string, location: string) => {
  try {
    Clarity.event('brand_interaction');
    Clarity.setTag('brand_name', brandName);
    Clarity.setTag('brand_location', location);
  } catch (error) {
    console.error('Clarity brand tracking error:', error);
  }
};

// Quote request tracking
export const clarityTrackQuoteRequest = (projectType: string, formData: any) => {
  try {
    Clarity.event('quote_request');
    Clarity.setTag('project_type', projectType);
    Clarity.setTag('has_vehicle_info', !!(formData.vehicleBrand && formData.vehicleModel));
    Clarity.setTag('budget_range', formData.budget || 'not_specified');
    Clarity.setTag('project_timeline', formData.timeline || 'not_specified');
    
    // High value session - upgrade for detailed recording
    Clarity.upgrade(`Quote request for ${projectType}`);
  } catch (error) {
    console.error('Clarity quote tracking error:', error);
  }
};

// Admin access tracking
export const clarityTrackAdminAccess = (action: string, userId?: string) => {
  try {
    Clarity.event('admin_access');
    Clarity.setTag('admin_action', action);
    if (userId) Clarity.setTag('admin_user', userId);
    
    // Upgrade admin sessions for detailed analysis
    if (action === 'login_success') {
      Clarity.upgrade('Admin login detected');
    }
  } catch (error) {
    console.error('Clarity admin tracking error:', error);
  }
};

// Error tracking
export const clarityTrackError = (error: Error, component: string, additionalData?: any) => {
  try {
    Clarity.event('javascript_error');
    Clarity.setTag('error_component', component);
    Clarity.setTag('error_message', error.message.substring(0, 100));
    Clarity.setTag('error_type', error.name);
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        if (typeof additionalData[key] === 'string') {
          Clarity.setTag(`error_${key}`, additionalData[key]);
        }
      });
    }
    
    // Upgrade sessions with errors for debugging
    Clarity.upgrade(`JavaScript error in ${component}`);
  } catch (clarityError) {
    console.error('Clarity error tracking error:', clarityError);
  }
};

// Performance tracking
export const clarityTrackPerformance = (metric: string, value: number, unit: string = 'ms') => {
  try {
    Clarity.event('performance_metric');
    Clarity.setTag('performance_metric', metric);
    Clarity.setTag('performance_value', value.toString());
    Clarity.setTag('performance_unit', unit);
    
    // Upgrade slow sessions for performance analysis
    if (metric === 'page_load_time' && value > 3000) {
      Clarity.upgrade('Slow page load detected');
    }
  } catch (error) {
    console.error('Clarity performance tracking error:', error);
  }
};

// Scroll depth tracking
export const clarityTrackScrollDepth = (percentage: number) => {
  try {
    Clarity.event('scroll_depth');
    Clarity.setTag('scroll_percentage', percentage.toString());
    Clarity.setTag('page_url', window.location.pathname);
  } catch (error) {
    console.error('Clarity scroll tracking error:', error);
  }
};

// Search tracking (for FAQ, etc.)
export const clarityTrackSearch = (query: string, results: number, context: string) => {
  try {
    Clarity.event('search_performed');
    Clarity.setTag('search_query', query.substring(0, 50)); // Truncate for privacy
    Clarity.setTag('search_results', results.toString());
    Clarity.setTag('search_context', context);
  } catch (error) {
    console.error('Clarity search tracking error:', error);
  }
};

// User journey tracking
export const clarityTrackUserJourney = (step: string, funnel: string) => {
  try {
    Clarity.event('user_journey');
    Clarity.setTag('journey_step', step);
    Clarity.setTag('journey_funnel', funnel);
    Clarity.setTag('journey_timestamp', new Date().toISOString());
  } catch (error) {
    console.error('Clarity user journey tracking error:', error);
  }
};

// Cookie consent tracking
export const claritySetCookieConsent = (hasConsent: boolean) => {
  try {
    Clarity.consent(hasConsent);
    Clarity.setTag('cookie_consent', hasConsent.toString());
  } catch (error) {
    console.error('Clarity consent tracking error:', error);
  }
};

// Helper function to categorize services
const getServiceCategory = (serviceName: string): string => {
  const serviceCategories = {
    'standard': ['sales', 'mounting', 'repair', 'storage', 'cleaning'],
    'premium': ['trackPrep', 'restoration', 'luxury', 'custom'],
    'mechanical': ['diagnostic', 'maintenance', 'brakes', 'oilChange', 'battery']
  };
  
  for (const [category, services] of Object.entries(serviceCategories)) {
    if (services.some(service => serviceName.toLowerCase().includes(service.toLowerCase()))) {
      return category;
    }
  }
  return 'other';
};

// Setup comprehensive tracking
export const setupClarity = () => {
  initClarity();
  
  if (typeof window !== 'undefined') {
    // Initial page identification
    clarityTrackPageView(window.location.pathname, document.title);
    
    // Set initial session tags
    Clarity.setTag('site_language', document.documentElement.lang || 'fr');
    Clarity.setTag('user_agent', navigator.userAgent);
    Clarity.setTag('screen_resolution', `${screen.width}x${screen.height}`);
    Clarity.setTag('viewport_size', `${window.innerWidth}x${window.innerHeight}`);
    
    // Track performance metrics
    window.addEventListener('load', () => {
      if ('performance' in window) {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          clarityTrackPerformance('page_load_time', navigation.loadEventEnd - navigation.fetchStart);
          clarityTrackPerformance('dom_content_loaded', navigation.domContentLoadedEventEnd - navigation.fetchStart);
          clarityTrackPerformance('first_paint', navigation.responseEnd - navigation.fetchStart);
        }
      }
    });
    
    // Set up scroll tracking
    let scrollTracked = { 25: false, 50: false, 75: false, 90: false };
    
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      Object.keys(scrollTracked).forEach(threshold => {
        const thresholdNum = parseInt(threshold);
        if (scrollPercent >= thresholdNum && !scrollTracked[thresholdNum]) {
          scrollTracked[thresholdNum] = true;
          clarityTrackScrollDepth(thresholdNum);
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Track JavaScript errors
    window.addEventListener('error', (event) => {
      clarityTrackError(event.error, 'Global Error Handler', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });
    
    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      clarityTrackError(new Error(event.reason), 'Unhandled Promise Rejection');
    });
    
    // Track viewport changes (responsive behavior)
    window.addEventListener('resize', () => {
      Clarity.setTag('viewport_size', `${window.innerWidth}x${window.innerHeight}`);
    });
  }
};
